import { isEmpty } from 'lodash';
import { MessagePlugin } from 'tdesign-react';

import { CopyType } from '@/const/lits';
import { AuditStatusDetailType, AuditStatusType } from '@/const/status';
import { exclStoryLine } from '@/editor/flowEditor/control/utils/exclStoryLine';
import { IStoryLineData } from '@/editor/flowEditor/control/utils/getStoryLineNodesEdges';
import { IStory, IStoryRes } from '@/interface';
import { ExistedFileInfo, ICosData } from '@/material/form-upload/uploadFileMatch';
import { isWoa } from '@/utils/getApiDomain';
import { postMaterial } from '@/utils/material';
import { transformObjectDeep } from '@/utils/tans/object';

import { apiPathServer } from './api-config';
import { ListCombinMode, SortRule } from './bot';
import { CgiErrorCode, CgiResp } from './cgi';
import {
  API_BOTH_PREFIX_TRPC,
  API_PREFIX,
  API_PREFIX_PLOT,
  request,
} from './request';

/**
 *
 * @param story map
 * @returns
 */

export const saveMap = async (id: string, draw: string) => {
  const res = await request(`${API_PREFIX_PLOT}${apiPathServer.postMap}`, {
    method: 'POST',
    body: transformObjectDeep(
      {
        chapter_id: id,
        draw,
      },
      'snake',
    ),
  });
  return res;
};

export const getStoryAuditMessage = async (storyId: string) => {
  const res = await request<CgiResp<{ failedReasons: string[] }>>(
    `${API_PREFIX_PLOT}${apiPathServer.GetStoryCheckOpinion}`,
    {
      query: {
        story_id: storyId,
      },
    },
  );
  return transformObjectDeep(res, 'camel') as CgiResp<{
    failedReasons: string[];
  }>;
};

export const getMap = async (chapterId: string, release = false) => {
  const res = await request<
  CgiResp<{
    draw: string;
  }>
  >(
    `${API_PREFIX_PLOT}${apiPathServer.mapDetail}${release ? '/release' : ''}`,
    {
      query: {
        chapter_id: chapterId,
      },
    },
  );
  const turnData = transformObjectDeep(res, 'camel') as CgiResp<string>;
  return {
    ...turnData,
  };
};

/**
 * 保存story信息
 */
export const modifyStoryDetail = async (story: Partial<IStory>, force?: boolean) => {
  const { materialMap, ...postData }: any = story;
  postData.materialMap = postMaterial(postData, materialMap);
  const res = await request(`${API_PREFIX}${apiPathServer.postStory}`, {
    method: 'POST',
    body: transformObjectDeep({
      force: force || false,
      data: postData,
    }, 'snake', true),
  });
  return transformObjectDeep(res, 'camel') as CgiResp<IStory>;
};

/**
 * copy项目信息
 */
export const copyStory = async (id: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.copy}`, {
    method: 'POST',
    body: { copy_id: id, type: CopyType.STORY },
  });
  return res;
};

/**
 * 删除story信息
 */
export const deleteStory = async (id: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.deleteStory}`, {
    method: 'POST',
    body: { id },
  });
  return res;
};

/**
 * 故事提交审核
 */
export const submitStory = async (id: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.submitStory}`, {
    method: 'POST',
    body: {
      story_id: id,
      next_status: AuditStatusDetailType.STATUS_DETAIL_TYPE_MANUAL_ING,
    },
  });
  return res;
};

/**
 * 故事预览二维码
 */
export const getStoryPreview = async (id: string) => {
  const res = await request<CgiResp<{ qrcode: string }>>(
    `${API_BOTH_PREFIX_TRPC}${apiPathServer.getStoryPreview}`,
    {
      method: 'POST',
      body: { story_id: id, source: 2 },
    },
  );
  if (res?.code !== 0) {
    throw new Error(res?.msg || res?.message || '预览二维码获取失败');
  }
  return res?.data?.qrcode || '';
};

/**
 * 运营审核
 */
export const auditStory = async (id: string, comment?: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.submitStory}`, {
    method: 'POST',
    body: {
      story_id: id,
      next_status: isEmpty(comment)
        ? AuditStatusDetailType.STATUS_DETAIL_TYPE_MANUAL_REVIEW_PASS
        : AuditStatusDetailType.STATUS_DETAIL_TYPE_MANUAL_REVIEW_FAIL,
      comment,
    },
  });
  return res;
};

/**
 * 运营上架故事
 */
export const publishStory = async (id: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.PublishStory}`, {
    method: 'POST',
    body: {
      story_id: id,
    },
  });
  return res;
};

/**
 * 拉取故事详情接口
 */
export const fetchStoryDetail = async ({ id = '', release = false }) => {
  const res = await request<CgiResp<IStory>>(
    `${API_PREFIX}${apiPathServer.storyDetail}${release ? '/release' : ''}`,
    {
      query: {
        id,
        depth: 3,
      },
    },
  );
  if (res?.code !== CgiErrorCode.NO_ERROR) {
    MessagePlugin.error(res?.message || '故事信息拉取失败，请稍后重试');
  }
  const turnData = transformObjectDeep(res?.data, 'camel') as IStory;
  return {
    ...res,
    scenes: turnData?.sceneList || [],
    bots: [],
    storyInfo: turnData,
  };
};

/**
 * 拉取故事线详情接口
 */
export const fetchStoryLine = async (id: string, chapterId: string, release = false) => {
  const res = await request<CgiResp<any>>(
    `${API_BOTH_PREFIX_TRPC}${apiPathServer.storyLine}`,
    {
      query: {
        story_id: id,
        chapter_id: chapterId,
        release,
      },
    },
  );

  const turnData = transformObjectDeep(res?.data, 'camel') as any;
  const storyLineData: IStoryLineData = exclStoryLine(turnData);
  return {
    ...res,
    data: storyLineData,
  };
};

/**
 * 拉取故事线选项接口
 */
export const getSceneSelection = async (
  storyId: string,
  sceneId: string,
  chapterId: string,
  release = false,
) => {
  const res = await request<CgiResp<any>>(
    `${API_BOTH_PREFIX_TRPC}${apiPathServer.getSceneSelection}`,
    {
      query: {
        story_id: storyId,
        scene_id: sceneId,
        chapter_id: chapterId,
        release,
      },
    },
  );

  const turnData = transformObjectDeep(res?.data, 'camel') as any;
  return {
    ...res,
    data: turnData,
  };
};

export const fetchStorySceneList = async (id: string, release = false) => {
  const res = await request<CgiResp<IStoryRes>>(
    `${API_PREFIX}${apiPathServer.storyDetail}${release ? '/release' : ''}`,
    {
      query: {
        id,
        depth: 2,
      },
    },
  );

  const turnData = transformObjectDeep(res?.data, 'camel') as IStory;
  return {
    list: turnData?.sceneList || [],
    pageInfo: {
      current: 1,
      pageSize: 999,
      total: turnData?.sceneList?.length || 0,
    },
  };
};

/**
 *  拉取列表
 * @param param0
 * @returns
 */
export enum StorySortType {
  STORYLISTSORTTYPE_SHOWINDEX_DEFAULT = 0,
  STORYLISTSORTTYPE_PLAY_COUNT, // 玩次数
  STORYLISTSORTTYPE_PLAYER_COUNT, // 玩家数
  STORYLISTSORTTYPE_PUBLISH_TIME, // 发布时间
  STORYLISTSORTTYPE_UPDATE_TIME,
}

export const fetchStoryList = async ({
  page = 1,
  size = 10,
  spaceId = '',
  sortType = StorySortType.STORYLISTSORTTYPE_PUBLISH_TIME,
  sortRule = SortRule.DESC,
  status = [],
  name,
  release = false,
  mode = ListCombinMode.MY_LIST,
}: {
  page?: number;
  size?: number;
  spaceId?: string;
  name?: string;
  sortType?: StorySortType;
  sortRule?: SortRule;
  status?: AuditStatusType[];
  release?: boolean;
  mode?: ListCombinMode;
}): Promise<{
  code?: number;
  message?: string;
  list: IStory[];
  pageInfo: {
    total: number;
    current: number;
    pageSize: number;
  };
}> => {
  const res = await request(
    `${API_PREFIX}${apiPathServer.storyList}${release ? '/release' : ''}`,
    {
      query: transformObjectDeep(
        {
          page,
          size,
          spaceId,
          name,
          sortConfig: JSON.stringify(transformObjectDeep(
            {
              sortType,
              sortRule,
            },
            'snake',
          )),
          status: isWoa
            ? undefined
            : JSON.stringify(status.map(item => Number(item))),
          detailStatus: isWoa
            ? JSON.stringify(status.map(item => Number(item)))
            : undefined,
          mode,
        },
        'snake',
      ),
    },
  );
  const { list = [], pageinfo: pageInfo = {} } = res?.data || {};
  return {
    code: res?.code,
    message: res?.message,
    list: list?.map((item: any) => transformObjectDeep(item, 'camel')) || [],
    pageInfo: {
      total: pageInfo?.total || 0,
      current: pageInfo?.page || 1,
      pageSize: pageInfo?.size || 10,
    },
  };
};

/**
 * 获取故事统计信息
 */
export const getStoryCount = async (id: string, release = false) => {
  const res = await request<CgiResp<any>>(
    `${API_PREFIX}${apiPathServer.GetStoryCount}${release ? '/release' : ''}`,
    {
      query: { id },
    },
  );
  return res;
};

/**
 * 资源查重
 */
export const checkResourceExist = async (storyId: string, hash: string) => {
  const res = await request<CgiResp<ICosData>>(
    `${API_PREFIX}${apiPathServer.checkResourceExist}`,
    {
      query: {
        story_id: storyId,
        hash,
      },
    },
  );
  return transformObjectDeep(res, 'camel') as CgiResp<ICosData>;
};

/**
 *  资源标记
 */
export const setResource = async (params: { storyId: string, hash: string, data: ICosData }) => {
  const res = await request<CgiResp<any>>(`${API_PREFIX}${apiPathServer.setResource}`, {
    method: 'POST',
    body: transformObjectDeep(params, 'snake'),
  });
  return transformObjectDeep(res, 'camel') as CgiResp<any>;
};

/**
 *  批量资源标记
 */
export const batchSetResource = (newUploads: ExistedFileInfo[], storyId: string) => Promise.all(newUploads.map(async (e) => {
  const { code, message } = await setResource({
    storyId,
    hash: e.hash,
    data: { cosUrl: e.url },
  });
  if (code !== CgiErrorCode.NO_ERROR) {
    console.error('资源标记失败:', message);
  }
}));
